import { zodResolver } from "@hookform/resolvers/zod";
import { isProjectPrivateSavingAllowed, selMessage } from "@selector/shared";
import { ProjectsSelect, ProjectTagMultiSelect, SelMessage } from "@selector/shared/components";
import { TrackEnum } from "@selector/shared/openapi";
import { DB_FIELD_MAX_LENGTH } from "@selector/shared/settings";
import { type ComponentProps, type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { Link, useLocation } from "react-router-dom";
import {
  Callout,
  CalloutIntent,
  DialogComponent,
  Item,
  MenuOptionItem,
  notify,
  Popover,
  Select,
  Switch,
  TextField,
  TriggerButton,
  TriggerButtonSize,
  VStack,
} from "widgets";
import { useFormAria, useOverlay } from "widgets/hooks";
import { number, object, string } from "zod";

import { type CreateGroupRequest, saveGroup } from "../../api/save/save-group.ts";
import { getValidateProjectCompatibility } from "../../api/save/save-load-view.ts";
import { syntenyViewerDefaultTracks } from "../../settings.ts";
import { useGenomeViewerStore } from "../../store/store.ts";
import type { FeaturesGroups, SubType, TrackType } from "../../types.ts";
import { getGroupTypeFromTrackType } from "../../utils/type-conversion.ts";

interface SavedFeaturesGroup {
  trackType: TrackType;
  subTypeId?: number;
  itemIds: number[];
}

enum SOURCE_OPTIONS {
  SYNTENY_VIEW = "SYNTENY_VIEW",
  GROUP = "GROUP",
}

type SourceOptionType = keyof typeof SOURCE_OPTIONS;

type SavePopoverProject = Parameters<NonNullable<ComponentProps<typeof ProjectsSelect>["onSelectionChange"]>>["0"];

interface SourceOptionData {
  sourceOption: SourceOptionType;
  groupData?: {
    trackType: TrackType;
    subType?: SubType;
    count: number;
  };
}

const SaveViewSchema = object({
  name: string({ required_error: "Name is required" })
    .trim()
    .min(1, "Name is required")
    .max(DB_FIELD_MAX_LENGTH.VIEW.NAME, `Name limited to ${DB_FIELD_MAX_LENGTH.VIEW.NAME} characters`),
  description: string()
    .trim()
    .max(
      DB_FIELD_MAX_LENGTH.VIEW.DESCRIPTION,
      `Description limited to ${DB_FIELD_MAX_LENGTH.VIEW.DESCRIPTION} characters`,
    )
    .optional(),
  projectId: number({ required_error: "Project is required" }),
  sourceOptionIndex: number().default(0),
});

export const SavePopover = () => {
  const { overlayProps, state } = useOverlay();

  const privateFieldLabel = `Private (Hidden from other project members)`;
  const [selectedProject, setSelectedProject] = useState<SavePopoverProject>();
  const privateSavingAllowed = selectedProject && isProjectPrivateSavingAllowed(selectedProject);
  const [updateView, setUpdateView] = useState<boolean>(false);
  const { pathname } = useLocation();

  // By default, the SYNTENY_VIEW is present as the first Source Option
  const [availableSourceOptions, setAvailableSourceOptions] = useState<SourceOptionData[]>([
    { sourceOption: SOURCE_OPTIONS.SYNTENY_VIEW },
  ]);
  const [featuresGroups, setFeaturesGroups] = useState<FeaturesGroups>(new Map());
  const [selectedGroup, setSelectedGroup] = useState<SavedFeaturesGroup>();
  const [selectedSourceOptionIndex, setSelectedSourceOptionIndex] = useState<number>(0);

  const tooltipTextForIsPrivate = useMemo(() => {
    if (selectedProject?.isPrivateWorkspace) {
      return selMessage({ id: "projects.private.itemsPrivacy" });
    } else if (!privateSavingAllowed) {
      return selMessage({ id: "projects.private.disabled" });
    }
    return privateFieldLabel;
  }, [privateFieldLabel, privateSavingAllowed, selectedProject?.isPrivateWorkspace]);

  const saveView = useGenomeViewerStore((state) => state.saveView);
  const syntenyId = useGenomeViewerStore((state) => state.syntenyOrganism.id);
  const viewMetadata = useGenomeViewerStore((state) => state.viewMetadata);
  const [submitting, setSubmitting] = useState(false);
  const [projectCompatible, setProjectCompatible] = useState(false);
  const getFeaturesGroups = useGenomeViewerStore((state) => state.getFeaturesGroups);

  const triggerRef = useRef<HTMLButtonElement>(null);

  const {
    handleSubmit,
    control,
    reset,
    getValues,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormAria({
    resolver: zodResolver(SaveViewSchema),
  });

  const handleSaveView = useCallback(async () => {
    clearErrors();
    const { name, description, projectTagIds, projectId, makePrivate } = getValues();

    const metadata = {
      id: updateView ? viewMetadata?.id : undefined,
      name,
      description,
      projectId,
      itemTagIds: projectTagIds ?? [],
      path: pathname.includes("/organism/") ? `/organism/${syntenyId}/synteny` : `/visualizer/synteny/${syntenyId}`,
      shared: !makePrivate,
    };
    saveView(metadata)
      .then(() => {
        setSubmitting(false);
        state.close();
        notify.info({
          title: selMessage({ id: "view.save.successTitle" }),
          children: (
            <SelMessage
              id={updateView && viewMetadata !== undefined ? "view.update.successMessage" : "view.save.successMessage"}
              values={{
                viewName: name,
                projectName: selectedProject?.name ?? "Unknown Project",
                // eslint-disable-next-line react/no-unstable-nested-components
                link: (chunk) =>
                  selectedProject ? (
                    <Link to={`/project/${selectedProject.id}/savedItems/view/view`}>{chunk}</Link>
                  ) : (
                    chunk
                  ),
              }}
            />
          ),
        });
      })
      .catch((error) => {
        setSubmitting(false);
        setError("root", { message: `The view "${name}" could not be created: ${error}` });
      });
  }, [
    clearErrors,
    getValues,
    pathname,
    saveView,
    selectedProject,
    setError,
    state,
    syntenyId,
    updateView,
    viewMetadata,
  ]);

  const handleSaveGroup = useCallback(async () => {
    if (!selectedGroup) {
      setSubmitting(false);
      return;
    }
    const { name, description, projectTagIds, projectId, makePrivate } = getValues();
    const metadata: CreateGroupRequest = {
      groupType: getGroupTypeFromTrackType(selectedGroup.trackType),
      subTypeId: selectedGroup.subTypeId,
      itemIds: selectedGroup.itemIds,
      name,
      description,
      projectId: projectId,
      shared: !makePrivate,
      itemTagIds: projectTagIds ?? [],
    };

    saveGroup(metadata)
      .then(({ id }) => {
        setSubmitting(false);
        state.close();
        notify.info({
          title: selMessage({ id: "view.save.successTitle" }),
          children: (
            <SelMessage
              id="group.save.successMessage"
              values={{
                groupName: name,
                // eslint-disable-next-line react/no-unstable-nested-components
                groupLink: (chunk) => <Link to={`/group/${id}`}>{chunk}</Link>,
                projectName: selectedProject?.name ?? "Unknown Project",
                // eslint-disable-next-line react/no-unstable-nested-components
                projectLink: (chunk) =>
                  selectedProject ? (
                    <Link to={`/project/${selectedProject.id}/savedItems/view/view`}>{chunk}</Link>
                  ) : (
                    chunk
                  ),
              }}
            />
          ),
        });
      })
      .catch(() => {
        setSubmitting(false);
        setError("root", { message: `The group ${name} could not be created.` });
      });
  }, [selectedGroup, getValues, state, selectedProject, setError]);

  const onSubmit = useCallback(async () => {
    if (!selectedProject) {
      return;
    }
    setSubmitting(true);
    // The SYNTENY_VIEW is always at the first index of the list, so it is safe to use the selectedSourceOptionIndex === 0
    if (selectedSourceOptionIndex === 0) {
      handleSaveView();
    } else {
      handleSaveGroup();
    }
  }, [handleSaveView, handleSaveGroup, selectedProject, selectedSourceOptionIndex]);

  const onFormSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      handleSubmit(onSubmit)();
    },
    [handleSubmit, onSubmit],
  );

  const computeAndSetFeaturesGroups = useCallback(() => {
    // Converts the selected features from the canvas to an exportable payload
    const featuresMap: FeaturesGroups = getFeaturesGroups();
    const sourceOptions: SourceOptionData[] = [{ sourceOption: SOURCE_OPTIONS.SYNTENY_VIEW }];

    for (const [trackType, featureGroups] of featuresMap) {
      if (trackType === TrackEnum.NUCLEOTIDE_FEATURE) {
        for (const subTypeGroup of featureGroups) {
          const subType = subTypeGroup.subType!;
          sourceOptions.push({
            sourceOption: SOURCE_OPTIONS.GROUP,
            groupData: {
              trackType,
              subType: subType,
              count: subTypeGroup.featureIds.length,
            },
          });
        }
      } else if (trackType !== TrackEnum.SEQUENCE) {
        // Saving of region groups is not yet planned to be implemented on MT
        sourceOptions.push({
          sourceOption: SOURCE_OPTIONS.GROUP,
          groupData: {
            trackType,
            count: featureGroups?.reduce((acc, track) => acc + track.featureIds.length, 0),
          },
        });
      }
    }

    setFeaturesGroups(featuresMap);
    setAvailableSourceOptions(sourceOptions);
    setSubmitting(false);
  }, [getFeaturesGroups]);

  useEffect(() => {
    if (updateView && selectedProject && viewMetadata) {
      // This is a hack to ensure that the form resets `projectTagIds`
      // to the right value.
      setValue("projectTagIds", viewMetadata.itemTagIds);
      clearErrors();
    }
  }, [clearErrors, selectedProject, setValue, updateView, viewMetadata]);

  const resetToDefaults = useCallback(() => {
    reset({ name: "", description: "", makePrivate: false, projectId: null, projectTagIds: [] });
    setSelectedSourceOptionIndex(0);
    clearErrors();
  }, [clearErrors, reset]);

  useEffect(() => {
    if (!selectedProject) {
      return;
    }

    clearErrors();

    getValidateProjectCompatibility(selectedProject.id, syntenyId!)
      .then((validationResult) => {
        const isCompatible = validationResult?.isCompatible;
        if (!isCompatible) {
          setProjectCompatible(false);
          setError(
            "projectId",
            {
              message: "Usergroup of project incompatible with the Pangenome",
            },
            { shouldFocus: true },
          );
        } else {
          setProjectCompatible(true);
          // Clear the error if validation passes
          clearErrors("projectId");
        }
      })
      .catch((e) => setError("root", { message: e.message }));
  }, [setError, clearErrors, selectedProject, syntenyId, setProjectCompatible]);

  return (
    <>
      <TriggerButton
        size={TriggerButtonSize.Normal}
        icon={"bookmark_add"}
        onPress={() => {
          setUpdateView(false);
          setSelectedProject(undefined);
          // Note that I'm resetting the `projectId` to `null`.
          // The widget makes a difference between `undefined` and `null`.
          resetToDefaults();
          computeAndSetFeaturesGroups();
          state.open();
        }}
        label={"Save"}
        showLabel
        ref={triggerRef}
      />
      <Popover state={state} {...overlayProps} triggerRef={triggerRef} placement="bottom end">
        <DialogComponent
          data-testid="save-popover"
          mainButton={{
            label: "Save",
            onPress: () => handleSubmit(onSubmit)(),
            isDisabled: selectedProject === undefined || !projectCompatible,
            isLoading: submitting,
          }}
          closeFn={() => {
            // Only close the popover if we are not submitting.
            if (!submitting) {
              state.close();
            }
          }}
          width={500}
          asPanelHeader
          errorCallout={errors.root?.message ? { message: errors.root.message } : undefined}
        >
          <form data-testid="save-form" onSubmit={onFormSubmit}>
            <VStack spacing={15}>
              {viewMetadata?.id && selectedSourceOptionIndex === 0 && (
                <Switch
                  isSelected={updateView}
                  onChange={(value) => {
                    setUpdateView(value);
                    if (value) {
                      reset({
                        name: viewMetadata.name,
                        description: viewMetadata.description ?? "",
                        makePrivate: !viewMetadata.shared,
                        projectId: viewMetadata.projectId,
                        projectTagIds: viewMetadata.itemTagIds,
                      });
                    } else {
                      // Note that I'm resetting the `projectId` to `null`.
                      // The widget makes a difference between `undefined` and `null`.
                      resetToDefaults();
                    }
                  }}
                  label="Update current view"
                />
              )}
              {viewMetadata?.id && updateView && (
                <Callout key="info" intent={CalloutIntent.Info} style={{ marginTop: "-5px" }}>
                  The previously saved view will be overwritten. Project and visibility are preserved.
                </Callout>
              )}
              <Controller
                control={control}
                name="sourceOption"
                render={({ field }) => (
                  <Select
                    label="Save"
                    isRequired
                    selectedKey={String(field.value ?? 0)}
                    items={availableSourceOptions}
                    onSelectionChange={(key) => {
                      const numKey = Number(key);
                      field.onChange(numKey);
                      setSelectedSourceOptionIndex(numKey);
                      const selectedSource = availableSourceOptions[numKey];

                      if (!selectedSource) return;

                      const groupData = selectedSource.groupData;
                      // When the sourceOption is a GROUP of features, then set the selectedGroup to the group of the feature like all PROTEIN_LOCATIONS, etc.
                      if (groupData) {
                        const trackType = groupData.trackType;
                        const subType = groupData.subType;
                        const featuresGroup: SavedFeaturesGroup = {
                          trackType: trackType,
                          subTypeId: subType?.id,
                          itemIds:
                            featuresGroups
                              .get(trackType)
                              ?.filter((group) => (group.subType ? group.subType?.id === subType?.id : true))
                              .flatMap((group) => group.featureIds) ?? [],
                        };
                        setSelectedGroup(featuresGroup);
                      }
                    }}
                    width={180}
                  >
                    {(item) => {
                      const title = getSourceOptionTitle(item);
                      return (
                        <Item key={String(availableSourceOptions.indexOf(item) ?? 0)} textValue={title}>
                          <MenuOptionItem title={title} />
                        </Item>
                      );
                    }}
                  </Select>
                )}
                defaultValue={undefined}
              />
              <Controller
                control={control}
                name="projectId"
                render={({ field }) => (
                  <ProjectsSelect
                    label="Save to"
                    isRequired
                    onSelectionChange={(project) => {
                      field.onChange(project?.id);
                      // If the project is not private, then make it public by default.
                      setValue("makePrivate", !!project?.isPrivateWorkspace);
                      setSelectedProject(project);
                    }}
                    isDisabled={submitting || updateView}
                    initialSelectedProjectId={field.value}
                    errorMessage={errors.projectId?.message?.toString()}
                    isInvalid={!!errors.projectId}
                    defaultSelection="previous"
                  />
                )}
                defaultValue={undefined}
              />
              <Controller
                name="makePrivate"
                control={control}
                render={({ field }) => (
                  <Switch
                    isSelected={field.value}
                    onChange={field.onChange}
                    tooltip={tooltipTextForIsPrivate}
                    label={privateFieldLabel}
                    isDisabled={
                      submitting || updateView || selectedProject?.isPrivateWorkspace || !privateSavingAllowed
                    }
                  />
                )}
                defaultValue={true}
              />
              <Controller
                render={({ field }) => (
                  <TextField
                    label="Name"
                    isRequired
                    isInvalid={!!errors.name}
                    errorMessage={errors.name?.message?.toString()}
                    data-testid="name"
                    placeholder={`Enter ${selectedSourceOptionIndex === 0 ? "view" : "group"} name`}
                    value={field.value}
                    onChange={field.onChange}
                    isDisabled={submitting}
                  />
                )}
                defaultValue=""
                name="name"
                control={control}
              />
              <Controller
                render={({ field }) => (
                  <TextField
                    label="Description"
                    data-testid="description"
                    rows={5}
                    value={field.value}
                    onChange={field.onChange}
                    isDisabled={submitting}
                  />
                )}
                defaultValue=""
                name="description"
                control={control}
              />
              <Controller
                name="projectTagIds"
                control={control}
                render={({ field }) => {
                  return (
                    <ProjectTagMultiSelect
                      label="Tags"
                      projectId={selectedProject?.id}
                      onSelectionChange={field.onChange}
                      selectedIds={field.value}
                      allowAddingTags
                      isDisabled={submitting || !selectedProject}
                    />
                  );
                }}
                defaultValue={[]}
              />
            </VStack>
          </form>
        </DialogComponent>
      </Popover>
    </>
  );
};

function getSourceOptionTitle(sourceData: SourceOptionData): string {
  const groupData = sourceData.groupData;
  switch (sourceData.sourceOption) {
    case SOURCE_OPTIONS.SYNTENY_VIEW:
      return "Synteny View";
    case SOURCE_OPTIONS.GROUP:
      if (groupData) {
        const subTypeName = groupData.subType?.name;
        const trackName =
          subTypeName ?? syntenyViewerDefaultTracks.find((tracks) => tracks.type === groupData.trackType)?.name ?? "";
        return `${trackName} Group - ${groupData.count} selected`;
      } else {
        return sourceData.sourceOption;
      }
    default:
      return sourceData.sourceOption;
  }
}
